import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  Input,
  VStack,
  Flex,
  Text,
  Spinner,
  Checkbox,
  Collapse,
  IconButton,
  Button,
  Menu,
  MenuButton,
  MenuList,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useColorModeValue,
  Heading,
  Badge,
  HStack,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon, AddIcon, CheckIcon, MinusIcon } from '@chakra-ui/icons';
import api from '../api';
import ProductDetailModal from './ProductDetailModal';

const InvoiceProductSidebar = ({
  filters,
  setFilters,
  categories,
  selectedCategories,
  setSelectedCategories,
  handleAddProduct,
  handleRemoveProduct,
  selectedProducts,
}) => {
  // Color mode values for modern styling
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtleTextColor = useColorModeValue('gray.600', 'gray.400');
  const shadowColor = useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(0, 0, 0, 0.3)');
  const hoverBg = useColorModeValue('gray.100', 'gray.700');
  const itemBg = useColorModeValue('gray.50', 'gray.700');
  const selectedBg = useColorModeValue('green.50', 'green.900');
  const selectedHoverBg = useColorModeValue('green.100', 'green.800');
  const selectedBorderColor = useColorModeValue('#22C55E', '#16A34A');
  const selectedHoverBorderColor = useColorModeValue('#16A34A', '#15803D');
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isProductDetailOpen, setIsProductDetailOpen] = useState(false);
  const [isFullImageOpen, setIsFullImageOpen] = useState(false);
  const [fullImageSrc, setFullImageSrc] = useState('');
  const placeholderImage = '/placeholder-product.jpeg';

  useEffect(() => {
    fetchProducts(1, selectedCategories);
  }, [filters.name, selectedCategories]);

  const fetchProducts = async (page, categoryIds = []) => {
    setLoading(true);
    try {
      const response = await api.get(
        `/products/?page=${page}&search=${filters.name}&categories=${categoryIds.join(',')}`
      );
      const newProducts = response.data.results;
      setProducts((prev) => (page === 1 ? newProducts : [...prev, ...newProducts]));
      setHasMore(!!response.data.next);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = (e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
    if (bottom && hasMore && !loading) {
      fetchProducts(currentPage + 1, selectedCategories);
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handleCategoryChange = (categoryId, category) => {
    const collectChildCategoryIds = (cat) => {
      let ids = [cat.id];
      if (cat.children) {
        cat.children.forEach((child) => {
          ids = ids.concat(collectChildCategoryIds(child));
        });
      }
      return ids;
    };

    const childIds = collectChildCategoryIds(category);

    if (selectedCategories.some((id) => childIds.includes(id))) {
      // Remove all child categories
      setSelectedCategories((prev) => prev.filter((id) => !childIds.includes(id)));
    } else {
      // Add all child categories
      setSelectedCategories((prev) => [...prev, ...childIds]);
    }
  };

  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const isCategorySelected = (category) => {
    const collectChildCategoryIds = (cat) => {
      let ids = [cat.id];
      if (cat.children) {
        cat.children.forEach((child) => {
          ids = ids.concat(collectChildCategoryIds(child));
        });
      }
      return ids;
    };

    const childIds = collectChildCategoryIds(category);
    const selectedChildren = childIds.filter((id) => selectedCategories.includes(id));

    if (selectedChildren.length === childIds.length) {
      return true; // Fully selected
    }
    if (selectedChildren.length > 0) {
      return 'indeterminate'; // Partially selected
    }
    return false; // Not selected
  };

  const renderCategories = (categories, depth = 0) =>
    categories.map((category) => {
      const categoryState = isCategorySelected(category);
      return (
        <Box key={category.id} mb="2" ml={depth * 4}>
          <Flex align="center">
            {category.children?.length > 0 && (
              <IconButton
                icon={
                  expandedCategories.includes(category.id) ? (
                    <ChevronDownIcon />
                  ) : (
                    <ChevronRightIcon />
                  )
                }
                size="sm"
                onClick={() => toggleCategoryExpansion(category.id)}
                variant="ghost"
                aria-label="Toggle Category"
                mr="2"
              />
            )}
            <Checkbox
              isChecked={categoryState === true}
              isIndeterminate={categoryState === 'indeterminate'}
              onChange={() => handleCategoryChange(category.id, category)}
            >
              {category.name}
            </Checkbox>
          </Flex>
          {category.children?.length > 0 && (
            <Collapse in={expandedCategories.includes(category.id)} animateOpacity>
              {renderCategories(category.children, depth + 1)}
            </Collapse>
          )}
        </Box>
      );
    });

  const handleViewProductDetails = (product, event) => {
    // Don't open detail modal if clicking on the add button
    if (event.target.closest('button')) {
      return;
    }
    setSelectedProduct(product);
    setIsProductDetailOpen(true);
  };

  const handleCloseProductModal = () => {
    setSelectedProduct(null);
    setIsProductDetailOpen(false);
  };

  const openFullImage = (imageSrc) => {
    setFullImageSrc(imageSrc);
    setIsFullImageOpen(true);
  };

  const closeFullImage = () => {
    setIsFullImageOpen(false);
    setFullImageSrc('');
  };

  return (
    <Box h="100%" display={{ base: 'none', md: 'block' }}>
      {/* Header Section */}
      <Box mb={4} display={{ base: 'none', md: 'block' }}>
        <Text 
          fontSize="lg" 
          fontWeight="bold" 
          color={textColor}
          bgGradient="linear(to-r, #4facfe, #00f2fe)"
          bgClip="text"
          mb={4}
        >
          🛍️ Додати товари
        </Text>
      </Box>

      {/* Search and Category Filters Card */}
      <Box 
        bg={cardBg}
        borderRadius="lg"
        border="1px solid"
        borderColor={borderColor}
        p={4}
        mb={4}
        boxShadow={`0 4px 6px -1px ${shadowColor}`}
        position="relative"
        overflow="visible"
        zIndex={10}
        display={{ base: 'none', md: 'block' }}
      >
        {/* Filter accent bar */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          height="3px"
          bg="linear-gradient(90deg, #4facfe, #00f2fe)"
        />
        
        <Text 
          fontSize="sm" 
          fontWeight="600" 
          color={textColor} 
          mb={3}
          mt={1}
        >
          🔍 Фільтри
        </Text>

        {/* Search Filter */}
        <FormControl mb={3}>
          <Input
            placeholder="🔍 Пошук за назвою"
            value={filters.name}
            onChange={(e) => setFilters({ ...filters, name: e.target.value })}
            bg={cardBg}
            border="1px solid"
            borderColor={borderColor}
            borderRadius="lg"
            size="sm"
            _focus={{
              borderColor: "#4facfe",
              boxShadow: "0 0 0 1px rgba(79, 172, 254, 0.3)",
            }}
            _placeholder={{
              color: subtleTextColor,
            }}
          />
        </FormControl>

        {/* Category Selector Dropdown */}
        <FormControl>
          <Menu>
            <MenuButton 
              as={Button} 
              rightIcon={<ChevronDownIcon />} 
              w="100%"
              size="sm"
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="lg"
              color={textColor}
              _hover={{ 
                bg: hoverBg,
                borderColor: "#4facfe"
              }}
              _active={{
                bg: hoverBg
              }}
            >
              📂 Обрати категорії
            </MenuButton>
            <MenuList 
              p={4} 
              maxH="300px" 
              overflowY="auto" 
              w="115%"
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="lg"
              boxShadow={`0 10px 15px -3px ${shadowColor}`}
              zIndex={9999}
              position="relative"
            >
              {categories.length > 0 
                ? renderCategories(categories) 
                : <Text color={subtleTextColor}>Категорії не знайдено</Text>}
            </MenuList>
          </Menu>
        </FormControl>
      </Box>

      {/* Product List Card */}
      <Box 
        bg={cardBg}
        borderRadius="lg"
        border="1px solid"
        borderColor={borderColor}
        boxShadow={`0 4px 6px -1px ${shadowColor}`}
        position="relative"
        overflow="hidden"
        h="calc(100% - 200px)"
        display={{ base: 'none', md: 'flex' }}
        flexDirection="column"
      >
        {/* Products accent bar */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          height="3px"
          bg="linear-gradient(90deg, #f093fb, #f5576c)"
        />
        
        <Box p={4} borderBottom="1px solid" borderColor={borderColor} display={{ base: 'none', md: 'block' }} mb={{ base: 0, md: 2 }}>
          <Text 
            fontSize="sm" 
            fontWeight="600" 
            color={textColor}
            mt={1}
          >
            📦 Товари
          </Text>
        </Box>
        
        {/* Product List */}
        <Box flex="1" overflowY="auto" p={2} onScroll={handleScroll}>
          {products.length > 0 ? (
            <VStack spacing={2} align="stretch">
              {products.map((product) => (
                <Box
                  key={product.id}
                  p={3}
                  borderWidth="2px"
                  borderColor={selectedProducts.includes(product.id) ? selectedBorderColor : borderColor}
                  borderRadius="lg"
                  bg={selectedProducts.includes(product.id) ? selectedBg : itemBg}
                  _hover={{ 
                    bg: selectedProducts.includes(product.id) ? selectedHoverBg : hoverBg,
                    borderColor: selectedProducts.includes(product.id) ? selectedHoverBorderColor : "#f093fb",
                    transform: "translateY(-1px)",
                    boxShadow: `0 4px 8px ${shadowColor}`
                  }}
                  cursor="pointer"
                  transition="all 0.2s"
                  onClick={(e) => handleViewProductDetails(product, e)}
                >
                  <Flex align="center" justify="space-between" gap={3}>
                    <Image
                      src={product.images?.[0]?.image || placeholderImage}
                      alt={product.name}
                      boxSize="40px"
                      objectFit="cover"
                      borderRadius="md"
                      cursor="pointer"
                      flexShrink={0}
                      border="1px solid"
                      borderColor={borderColor}
                    />

                    <Box flex="1" minW="0">
                      <Text 
                        fontWeight="600" 
                        fontSize="sm"
                        color={textColor}
                        isTruncated={false} 
                        whiteSpace="normal" 
                        wordBreak="break-word"
                        lineHeight="1.2"
                      >
                        {product.name}
                      </Text>
                      <Flex align="center" gap={3} mt={1}>
                        <Text fontSize="xs" color={subtleTextColor}>
                          💰 {product.price} грн
                        </Text>
                        <Text fontSize="xs" color={subtleTextColor}>
                          📦 {product.stock_quantity} шт
                        </Text>
                      </Flex>
                      <HStack spacing={2} mt={1}>
                        <Badge colorScheme="blue" borderRadius="md" px={2} fontSize="2xs">
                          ID: {product.id}
                        </Badge>
                        {product.catalog_number && (
                          <Badge colorScheme="purple" borderRadius="md" px={2} fontSize="2xs">
                            {product.catalog_number}
                          </Badge>
                        )}
                      </HStack>
                    </Box>

                    {selectedProducts.includes(product.id) ? (
                      <IconButton
                        icon={<MinusIcon />}
                        onClick={() => handleRemoveProduct && handleRemoveProduct(product.id)}
                        colorScheme="red"
                        size="sm"
                        borderRadius="lg"
                        _hover={{
                          transform: "scale(1.1)",
                        }}
                        transition="all 0.2s"
                        aria-label="Remove product"
                      />
                    ) : (
                      <IconButton
                        icon={<AddIcon />}
                        onClick={() => handleAddProduct(product)}
                        colorScheme="blue"
                        size="sm"
                        borderRadius="lg"
                        _hover={{
                          transform: "scale(1.1)",
                        }}
                        transition="all 0.2s"
                      />
                    )}
                  </Flex>
                </Box>
              ))}
            </VStack>
          ) : (
            <Box 
              textAlign="center" 
              py={8}
              color={subtleTextColor}
            >
              <Text fontSize="lg" mb={2}>📭</Text>
              <Text fontSize="sm">Товарів не знайдено</Text>
            </Box>
          )}
          {loading && (
            <Box textAlign="center" py={4}>
              <Spinner 
                size="md" 
                color="blue.500"
                thickness="3px"
              />
              <Text fontSize="sm" color={subtleTextColor} mt={2}>
                Завантаження...
              </Text>
            </Box>
          )}
        </Box>
      </Box>

      {/* Product Detail Modal */}
      <ProductDetailModal
        isOpen={isProductDetailOpen}
        onClose={handleCloseProductModal}
        product={selectedProduct}
        onOpenFullImage={openFullImage}
      />

      {/* Full Image Modal */}
      <Modal isOpen={isFullImageOpen} onClose={closeFullImage} size="6xl" isCentered>
        <ModalOverlay bg="blackAlpha.800" onClick={closeFullImage} />
        <ModalContent bg="transparent" boxShadow="none" maxW="95vw" maxH="95vh" position="relative">
          <ModalBody p={0} display="flex" alignItems="center" justifyContent="center" position="relative" onClick={closeFullImage}>
            <Box position="relative" maxW="100%" maxH="100%" onClick={(e) => e.stopPropagation()}>
              <Image
                src={fullImageSrc}
                alt="Full size"
                maxW="100%"
                maxH="95vh"
                objectFit="contain"
                borderRadius="lg"
                onClick={closeFullImage}
                cursor="pointer"
              />
              {/* Close Button positioned on top-right corner of image */}
              <IconButton
                icon={<Text fontSize="lg">✕</Text>}
                aria-label="Close full image"
                position="absolute"
                top="10px"
                right="10px"
                colorScheme="whiteAlpha"
                bg="blackAlpha.600"
                color="white"
                size="lg"
                borderRadius="full"
                onClick={closeFullImage}
                _hover={{ bg: 'blackAlpha.800' }}
              />
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default InvoiceProductSidebar;
